import React, { useState, useEffect } from 'react';
import { Cog6ToothIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { getCostConfig, calculateCost, saveCostConfig, resetCostConfig } from '../utils/costCalculator';

function Settings() {
  const [costConfig, setCostConfig] = useState(getCostConfig());
  const [testUsage, setTestUsage] = useState(50000);
  const [calculatedCost, setCalculatedCost] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Función para calcular el costo usando la utilidad
  const calculateCurrentCost = (used) => {
    return calculateCost(used, costConfig);
  };

  // Actualizar el costo calculado cuando cambie la configuración o el uso de prueba
  useEffect(() => {
    setCalculatedCost(calculateCurrentCost(testUsage));
  }, [costConfig, testUsage]);

  // Cargar configuración guardada al montar el componente
  useEffect(() => {
    setCostConfig(getCostConfig());
  }, []);

  const handleConfigChange = (field, value) => {
    setCostConfig(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Guardar usando la utilidad
      const success = saveCostConfig(costConfig);
      
      if (success) {
        // Aquí podrías agregar una llamada a la API para guardar en la base de datos
        // await fetch('/api/settings/cost-config', {
        //   method: 'POST',
        //   headers: { 'Content-Type': 'application/json' },
        //   body: JSON.stringify(costConfig)
        // });
        
        setSaveMessage('Configuración guardada exitosamente');
      } else {
        setSaveMessage('Error al guardar la configuración');
      }
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      setSaveMessage('Error al guardar la configuración');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    resetCostConfig();
    setCostConfig(getCostConfig());
    setSaveMessage('Configuración restablecida a valores por defecto');
    setTimeout(() => setSaveMessage(''), 3000);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center">
            <Cog6ToothIcon className="h-8 w-8 text-gray-400 mr-3" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Configuración</h1>
              <p className="mt-1 text-sm text-gray-600">
                Configura los parámetros para el cálculo de costos de envío
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Configuración de Costos */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center mb-4">
            <CurrencyDollarIcon className="h-6 w-6 text-green-500 mr-2" />
            <h2 className="text-lg font-medium text-gray-900">Configuración de Costos</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Costo Base */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Costo Base ($)
              </label>
              <input
                type="number"
                step="0.01"
                value={costConfig.baseCost}
                onChange={(e) => handleConfigChange('baseCost', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Costo base para el umbral de envíos
              </p>
            </div>

            {/* Umbral */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Umbral de Envíos
              </label>
              <input
                type="number"
                value={costConfig.threshold}
                onChange={(e) => handleConfigChange('threshold', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Número de envíos incluidos en el costo base
              </p>
            </div>

            {/* Costo Adicional */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Costo Adicional por Envío ($)
              </label>
              <input
                type="number"
                step="0.0001"
                value={costConfig.additionalCostPerUnit}
                onChange={(e) => handleConfigChange('additionalCostPerUnit', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Costo por cada envío que exceda el umbral
              </p>
            </div>
          </div>

          {/* Calculadora de Prueba */}
          <div className="mt-8 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-md font-medium text-gray-900 mb-4">Calculadora de Prueba</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Número de Envíos de Prueba
                </label>
                <input
                  type="number"
                  value={testUsage}
                  onChange={(e) => setTestUsage(parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex items-end">
                <div className="w-full">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Costo Calculado
                  </label>
                  <div className="px-3 py-2 bg-white border border-gray-300 rounded-md text-lg font-semibold text-green-600">
                    ${calculatedCost.toFixed(2)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Botones de Acción */}
          <div className="mt-6 flex justify-between">
            <button
              onClick={handleReset}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Restablecer
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {isSaving ? 'Guardando...' : 'Guardar Configuración'}
            </button>
          </div>

          {/* Mensaje de Estado */}
          {saveMessage && (
            <div className={`mt-4 p-3 rounded-md ${
              saveMessage.includes('Error') 
                ? 'bg-red-50 text-red-700 border border-red-200' 
                : 'bg-green-50 text-green-700 border border-green-200'
            }`}>
              {saveMessage}
            </div>
          )}
        </div>
      </div>

      {/* Información de la Fórmula */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Fórmula de Cálculo</h3>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-700 mb-2">
              <strong>Si envíos ≤ {costConfig.threshold.toLocaleString()}:</strong>
            </p>
            <p className="text-sm text-gray-600 mb-4 ml-4">
              Costo = (${costConfig.baseCost} ÷ {costConfig.threshold.toLocaleString()}) × envíos
            </p>
            <p className="text-sm text-gray-700 mb-2">
              <strong>Si envíos {'>'} {costConfig.threshold.toLocaleString()}:</strong>
            </p>
            <p className="text-sm text-gray-600 ml-4">
              Costo = ${costConfig.baseCost} + ((envíos - {costConfig.threshold.toLocaleString()}) × ${costConfig.additionalCostPerUnit})
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Settings;